/**
 * O menu de dentro de "Mais".
 *
 * A barra de baixo tem cinco lugares e o produto tem nove telas. As quatro que
 * a mãe abre de vez em quando — remédios, compras, compromissos e o ajuste da
 * própria Ruth — moram aqui, em vez de disputar espaço com o dia dela.
 */

import { useEffect, useState } from 'react'

import { VERSAO, atualizarAgora, conferirAVersao, type Situacao } from '../atualizar'
import { Icone, type NomeDeIcone } from '../componentes/Icone'
import type { Tela } from '../componentes/Navegacao'
import '../estilos/mais.css'

type Item = { id: Tela; rotulo: string; sobre: string; icone: NomeDeIcone }

const ITENS: Item[] = [
  {
    id: 'remedios',
    rotulo: 'Remédios',
    sobre: 'O que está em andamento e o que é de todo dia',
    icone: 'remedio',
  },
  { id: 'compras', rotulo: 'Compras', sobre: 'A lista, sempre viva', icone: 'carrinho' },
  {
    id: 'compromissos',
    rotulo: 'Compromissos',
    sobre: 'O que já está marcado',
    icone: 'calendario',
  },
  {
    id: 'config',
    rotulo: 'Ajustar a Ruth',
    sobre: 'Quando ela fala, e como é a sua casa',
    icone: 'engrenagem',
  },
]

export function Mais({ aoAbrir }: { aoAbrir: (tela: Tela) => void }) {
  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">Mais</h1>
        </div>
      </header>

      <div className="corpo">
        {ITENS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="mais-item"
            onClick={() => aoAbrir(item.id)}
          >
            <span className="mais-item__icone">
              <Icone nome={item.icone} tamanho={22} />
            </span>
            <span className="mais-item__texto">
              <span className="mais-item__rotulo">{item.rotulo}</span>
              <span className="mais-item__sobre">{item.sobre}</span>
            </span>
            <span className="mais-item__seta">
              <Icone nome="seta" tamanho={18} />
            </span>
          </button>
        ))}

        <Atualizar />
      </div>
    </>
  )
}

const FRASE: Record<Situacao, string> = {
  'em dia': 'Você está na última versão',
  'tem versao nova': 'Tem uma versão nova',
  'nao consegui saber': 'Não consegui conferir agora',
}

/**
 * "Estou na última versão?" — respondida sem fé, e consertada num toque.
 *
 * Instalado na tela inicial do iPhone, o app não tem barra de endereço nem
 * botão de recarregar, e fechar não resolve: ele guarda o que baixou. Sem esta
 * linha a única saída era apagar o app e instalar de novo — e quem faz isso
 * perde o atalho e os avisos de dose junto.
 *
 * A conferência acontece sozinha ao abrir, porque a pergunta que importa não é
 * "quero atualizar?", é "estou atrasada?". O botão de atualizar só aparece
 * quando a resposta é sim: oferecer atualização a quem já está em dia é
 * convidar para um recarregamento que não muda nada.
 */
function Atualizar() {
  const [situacao, setSituacao] = useState<Situacao | null>(null)
  const [buscando, setBuscando] = useState(false)

  useEffect(() => {
    let vivo = true
    void conferirAVersao().then((r) => {
      if (vivo) setSituacao(r)
    })
    return () => {
      vivo = false
    }
  }, [])

  const atrasada = situacao !== 'em dia'

  return (
    <div className="mais-versao">
      {situacao && (
        <span
          className={`mais-versao__situacao ${
            situacao === 'em dia' ? 'mais-versao__situacao--ok' : ''
          }`}
          role="status"
        >
          {FRASE[situacao]}
        </span>
      )}

      {situacao !== null && atrasada && (
        <button
          type="button"
          className="mais-versao__botao"
          disabled={buscando}
          onClick={() => {
            setBuscando(true)
            void atualizarAgora()
          }}
        >
          {buscando ? 'Buscando…' : 'Atualizar o app'}
        </button>
      )}

      <span className="mais-versao__data">versão de {VERSAO}</span>
    </div>
  )
}
